import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import CanvasGrid from "../components/CanvasGrid";
import { computeSolar } from "../lib/solar";
import { defaultLot, computeCoverage, computeSetback } from "../lib/setback";
import { insetPolygon, polygonArea } from "../lib/geo";
import jsPDF from "jspdf";
import { autoFurnish, checkClearance, FURNISH_STYLES, nudgeClear } from "../lib/furnish";
import AIGeneratorModal from "../components/AIGeneratorModal";
import PhotoReconstructor from "../components/PhotoReconstructor";
import OrdinanceChecker from "../components/OrdinanceChecker";
import ExportModal from "../components/ExportModal";
import Walkthrough3D from "../components/Walkthrough3D";
import RenderModal from "../components/RenderModal";
import CostEstimator from "../components/CostEstimator";
import SiteDesigner from "../components/SiteDesigner";
import SiteMap from "../components/SiteMap";
import CrossSection from "../components/CrossSection";
import LivePreview from "../components/LivePreview";
import CodeResearch from "../components/CodeResearch";
import SolarScore from "../components/SolarScore";
import AIAssistant from "../components/AIAssistant";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { LAYERS, ITEM_KINDS, RUN_KINDS, totalArea, uid, WORLD_W, WORLD_H } from "../lib/blueprint";
import {
  Ruler, MousePointer2, Square, DoorOpen, RectangleHorizontal, Save, Download, Wand2, ScanLine,
  Landmark, Eye, EyeOff, Zap, Droplets, Wind, Layers, ZoomIn, ZoomOut, Trash2, Home, LayoutGrid, Loader2,
  Box, Share2, Image as ImageIcon, Building2, Plus, Calculator, Sofa, Mountain, Scissors, Sun, ChevronDown, AlertTriangle, Sunrise, Film, PenLine, Columns2, BookText, LandPlot, Sparkles, FilePlus2, Eraser, CheckCircle2, Map, FolderOpen, Pencil, Copy,
} from "lucide-react";
import { toast } from "sonner";

const TOOLS = [
  { id: "select", icon: MousePointer2, label: "Select / Move" },
  { id: "room", icon: Square, label: "Draw Room" },
  { id: "wall", icon: PenLine, label: "Draw Wall" },
  { id: "door", icon: DoorOpen, label: "Door" },
  { id: "window", icon: RectangleHorizontal, label: "Window" },
  { id: "duct-run", icon: Wind, label: "HVAC Supply Duct (draw)" },
  { id: "return-run", icon: Wind, label: "HVAC Return Duct (draw)" },
  { id: "pipe-run", icon: Droplets, label: "Water Supply Pipe (draw)" },
  { id: "waste-run", icon: Droplets, label: "Waste / Drain Pipe (draw)" },
];

const FIXTURES = {
  electrical: { icon: Zap, kinds: ["outlet", "switch", "panel", "light"] },
  plumbing: { icon: Droplets, kinds: ["sink", "toilet", "shower", "water", "drain"] },
  hvac: { icon: Wind, kinds: ["register", "duct", "condenser"] },
  engineering: { icon: Building2, kinds: ["column", "beam", "footing", "joist", "shearwall"] },
  furniture: { icon: Sofa, kinds: ["bed", "sofa", "table", "chair", "desk", "stove", "fridge", "bathtub", "wardrobe", "stair"] },
};

function MiniPlan({ blueprint }) {
  const rooms = (blueprint?.floors?.[0]?.rooms) || blueprint?.rooms || [];
  if (!rooms.length) {
    return <div className="w-9 h-9 rounded bg-[#0D1117] border border-[#30363D] flex items-center justify-center shrink-0"><LandPlot className="w-4 h-4 text-[#30363D]" /></div>;
  }
  const xs = rooms.flatMap((r) => [r.x, r.x + r.w]);
  const ys = rooms.flatMap((r) => [r.y, r.y + r.h]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const w = Math.max(1, maxX - minX), h = Math.max(1, maxY - minY);
  const sw = Math.max(0.4, Math.max(w, h) / 55);
  return (
    <svg viewBox={`${minX - 1} ${minY - 1} ${w + 2} ${h + 2}`} className="w-9 h-9 rounded bg-[#0D1117] border border-[#30363D] shrink-0" preserveAspectRatio="xMidYMid meet">
      {rooms.map((r) => (
        <rect key={r.id} x={r.x} y={r.y} width={r.w} height={r.h} fill="#38BDF8" fillOpacity="0.2" stroke="#38BDF8" strokeWidth={sw} />
      ))}
    </svg>
  );
}

export default function Studio() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [name, setName] = useState("Untitled Project");
  const [city, setCity] = useState("Custom");
  const [usState, setUsState] = useState("");
  const [codeResearch, setCodeResearch] = useState(null);
  const [codeOpen, setCodeOpen] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [conLoading, setConLoading] = useState(false);
  const [renderPath, setRenderPath] = useState(null);
  const [videoPath, setVideoPath] = useState(null);
  const [tourClips, setTourClips] = useState([]);
  const [videoShots, setVideoShots] = useState([]);
  const [sourcePhotoPath, setSourcePhotoPath] = useState(null);
  const [floors, setFloors] = useState([{ name: "Ground Floor", rooms: [], items: [] }]);
  const [activeFloor, setActiveFloor] = useState(0);
  const [tool, setTool] = useState("select");
  const [selectedId, setSelectedId] = useState(null);
  const [pxPerFt, setPxPerFt] = useState(13);
  const [activeLayers, setActiveLayers] = useState({ structural: true, electrical: true, plumbing: true, hvac: true, engineering: true, furniture: true });
  const [saving, setSaving] = useState(false);
  const [roofStyle, setRoofStyle] = useState("Gable");
  const [site, setSite] = useState({ slope_type: "Flat", slope_pct: 0 });
  const [siteOpen, setSiteOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [solarOpen, setSolarOpen] = useState(false);
  const [heatmap, setHeatmap] = useState(false);
  const [sunPath, setSunPath] = useState(false);
  const [season, setSeason] = useState("Equinox");
  const [splitView, setSplitView] = useState(false);
  const [showSetbacks, setShowSetbacks] = useState(false);
  const [lot, setLot] = useState(null);
  const [lotAddress, setLotAddress] = useState("");
  const [floorHeight, setFloorHeight] = useState(10);
  const [lotDialogOpen, setLotDialogOpen] = useState(false);
  const [lotEstimating, setLotEstimating] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [mode, setMode] = useState("site");
  const [showSiteMap, setShowSiteMap] = useState(true);
  const [gradeSuggestion, setGradeSuggestion] = useState(null);
  const [showShadows, setShowShadows] = useState(false);
  const [dimGuard, setDimGuard] = useState(false);
  const [sunTime, setSunTime] = useState(0.5);
  const [projMenuOpen, setProjMenuOpen] = useState(false);
  const [projMenuOpenM, setProjMenuOpenM] = useState(false);
  const [projList, setProjList] = useState([]);
  const [projLoading, setProjLoading] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [aiOpen, setAiOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [ordOpen, setOrdOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [renderOpen, setRenderOpen] = useState(false);
  const [renderAuto, setRenderAuto] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const skipDirty = useRef(true);
  const DRAFT_KEY = "archimesh_draft_v1";

  const _activeFloor = floors[activeFloor] || {};
  const blueprint = { name: _activeFloor.name, rooms: _activeFloor.rooms || [], items: _activeFloor.items || [], walls: _activeFloor.walls || [], runs: _activeFloor.runs || [] };
  const solar = useMemo(() => computeSolar(blueprint.rooms, blueprint.items, season), [blueprint, season]);
  const solarHours = useMemo(() => {
    const m = {};
    solar.scored.forEach((s) => { m[s.id] = s.sunHours; });
    return m;
  }, [solar]);
  const clearance = useMemo(() => checkClearance(blueprint.rooms, blueprint.items), [blueprint]);
  const effLot = useMemo(() => lot || defaultLot(blueprint.rooms), [lot, blueprint.rooms]);
  const worldW = useMemo(() => Math.max(WORLD_W, Math.ceil((effLot?.x || 0) + (effLot?.w || 0)) + 2), [effLot]);
  const worldH = useMemo(() => Math.max(WORLD_H, Math.ceil((effLot?.y || 0) + (effLot?.h || 0)) + 2), [effLot]);
  const effSetbacks = codeResearch?.setbacks_ft || site?.setbacks || null;
  const setbackViol = useMemo(() => (effSetbacks ? computeSetback(blueprint.rooms, effSetbacks, effLot) : null), [effSetbacks, blueprint.rooms, effLot]);

  const buildingH = useMemo(() => Math.max(1, floors.filter((f) => !["foundation", "basement"].includes(f.level)).length || 1) * (floorHeight || 10), [floors, floorHeight]);
  const shadow = useMemo(() => {
    const t = Math.min(1, Math.max(0, sunTime));
    const az = 90 + 180 * t; // sun azimuth: E(90) -> S(180) -> W(270)
    const alt = Math.max(6, 58 * Math.sin(Math.PI * t)); // altitude peaks at noon
    const screenAng = ((az + 180) - (Number(site?.orientation_deg) || 0)) * Math.PI / 180;
    const len = Math.min(buildingH / Math.tan(alt * Math.PI / 180), 220);
    return { dx: Math.sin(screenAng) * len, dy: -Math.cos(screenAng) * len, len: Math.round(len), altitude: Math.round(alt), hour: Math.round(6 + 12 * t) };
  }, [sunTime, site, buildingH]);
  const coverageLimit = codeResearch?.max_lot_coverage_pct || 50;
  const coverage = useMemo(() => computeCoverage(blueprint.rooms, effLot), [blueprint.rooms, effLot]);
  const hasDimTargets = useMemo(() => floors.some((f) => (f.rooms || []).some((r) => r.tw != null)), [floors]);
  const driftCount = useMemo(() => !dimGuard ? 0 : (blueprint.rooms || []).filter((r) => r.tw != null && r.th != null && (Math.abs(r.w - r.tw) > 0.25 || Math.abs(r.h - r.th) > 0.25)).length, [dimGuard, blueprint.rooms]);
  const lockDimTargets = () => setFloors((fs) => fs.map((f) => ({ ...f, rooms: (f.rooms || []).map((r) => ({ ...r, tw: r.w, th: r.h })) })));
  const toggleDimGuard = () => {
    if (!dimGuard && !hasDimTargets) { lockDimTargets(); toast.success("Locked current room sizes as targets — drift is now flagged"); }
    setDimGuard((v) => !v);
  };
  const setBlueprint = useCallback((updater) => {
    setFloors((fs) => fs.map((f, i) => i === activeFloor ? (typeof updater === "function" ? updater(f) : { name: f.name, ...updater }) : f));
  }, [activeFloor]);

  const persistBlueprint = () => ({ floors, rooms: blueprint.rooms, items: blueprint.items, roof_style: roofStyle, site, lot, lot_address: lotAddress, floor_height_ft: floorHeight, source_photo_path: sourcePhotoPath });

  const siteTerrainDesc = useCallback(() => {
    const parts = [];
    if (site?.slope_type && site.slope_type !== "Flat") parts.push(`${site.slope_type.toLowerCase()} lot at about ${site.slope_pct || 0}% grade`);
    else parts.push("a level lot");
    if (Number(site?.orientation_deg)) parts.push(`front oriented ${Math.round(site.orientation_deg)}° from north`);
    const featMap = { driveway: "a driveway", trees: "mature trees", water: "a water feature", corner: "" };
    const feats = [...new Set((site?.features || []).map((f) => featMap[f.kind]).filter(Boolean))];
    if (feats.length) parts.push(`site features include ${feats.join(", ")}`);
    const elevs = site?.spot_elevations || [];
    if (elevs.length >= 2) { const zs = elevs.map((e) => Number(e.z) || 0); parts.push(`spot elevations from ${Math.min(...zs)} to ${Math.max(...zs)} ft`); }
    return parts.join("; ");
  }, [site]);

  const addFloor = () => {
    const base = floors[activeFloor];
    const nf = {
      name: `Floor ${floors.length + 1}`,
      rooms: base.rooms.map((r) => ({ ...r, id: uid() })),
      items: base.items.map((it) => ({ ...it, id: uid() })),
    };
    setFloors((fs) => [...fs, nf]);
    setActiveFloor(floors.length);
    setSelectedId(null);
    toast.success("Floor added (aligned to floor below)");
  };

  const deleteFloor = () => {
    if (floors.length <= 1) return;
    const idx = activeFloor;
    setFloors((fs) => fs.filter((_, i) => i !== idx));
    setActiveFloor(Math.max(0, idx - 1));
    setSelectedId(null);
  };

  const applyProjectData = (p) => {
    skipDirty.current = true;
    setProjectId(p.id); setName(p.name); setCity(p.city || "Custom");
    setUsState(p.state || ""); setCodeResearch(p.code_research || null);
    if (p.blueprint?.floors?.length) setFloors(p.blueprint.floors);
    else setFloors([{ name: "Ground Floor", rooms: p.blueprint?.rooms || [], items: p.blueprint?.items || [] }]);
    setActiveFloor(0);
    const hasRooms = (p.blueprint?.floors || []).some((f) => (f.rooms || []).length) || (p.blueprint?.rooms || []).length;
    setMode(hasRooms ? "blueprint" : "site");
    setRoofStyle(p.blueprint?.roof_style || "Gable");
    setSite(p.blueprint?.site || { slope_type: "Flat", slope_pct: 0 });
    setLot(p.blueprint?.lot || null);
    setLotAddress(p.blueprint?.lot_address || "");
    setFloorHeight(p.blueprint?.floor_height_ft || 10);
    setSourcePhotoPath(p.blueprint?.source_photo_path || null);
    setRenderPath(p.render_path || null);
    setVideoPath(p.video_path || null);
    setSelectedId(null);
  };

  useEffect(() => {
    const pid = location.state?.projectId;
    if (pid) {
      api.get(`/projects/${pid}`).then((res) => applyProjectData(res.data)).catch(() => toast.error("Could not load project"));
      return;
    }
    if (location.state?.fresh) {
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      skipDirty.current = true;
      setFloors([{ name: "Ground Floor", rooms: [], items: [] }]);
      setProjectId(null); setName("Untitled Project"); setCity("Custom"); setUsState("");
      setCodeResearch(null); setLot(null); setLotAddress(""); setDirty(false);
      setSite({ slope_type: "Flat", slope_pct: 0 }); setMode("site");
      return;
    }
    // No project passed in — try to restore an unsaved local draft (safety net against reload/restart)
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && Array.isArray(d.floors) && d.floors.some((f) => (f.rooms || []).length)) {
          skipDirty.current = true;
          setProjectId(d.projectId || null);
          setName(d.name || "Untitled Project");
          setCity(d.city || "Custom");
          setUsState(d.usState || "");
          setCodeResearch(d.codeResearch || null);
          setFloors(d.floors);
          setActiveFloor(0);
          setMode("blueprint");
          setRoofStyle(d.roofStyle || "Gable");
          if (d.site) setSite(d.site);
          setLot(d.lot || null);
          setLotAddress(d.lotAddress || "");
          if (d.floorHeight) setFloorHeight(d.floorHeight);
          setDirty(true);
          toast.info("Restored your last unsaved draft — Save it to your account, or hit New to start over", { duration: 5000 });
        }
      }
    } catch { /* ignore bad draft */ }
  }, [location.state]);

  // Track unsaved changes
  useEffect(() => {
    if (skipDirty.current) { skipDirty.current = false; return; }
    setDirty(true);
  }, [floors, lot, site, roofStyle, floorHeight, name, city, codeResearch]);

  // Local draft backup so a reload/restart never loses work
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        projectId, name, city, usState, codeResearch, floors, lot, lotAddress, floorHeight, site, roofStyle,
      }));
    } catch { /* storage full/unavailable */ }
  }, [projectId, name, city, usState, codeResearch, floors, lot, lotAddress, floorHeight, site, roofStyle]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const save = async () => {
    setSaving(true);
    const payload = { name: (name || "").trim() || "Untitled Project", city, state: usState, code_research: codeResearch, blueprint: persistBlueprint(), render_path: renderPath, video_path: videoPath, description: "" };
    try {
      if (projectId) {
        await api.put(`/projects/${projectId}`, payload);
      } else {
        const res = await api.post("/projects", payload);
        setProjectId(res.data.id);
      }
      setDirty(false);
      setLastSaved(new Date());
      toast.success("Project saved to your account");
      return true;
    } catch { toast.error("Save failed — check your connection and try again"); return false; }
    finally { setSaving(false); }
  };

  const openSaveDialog = () => { setSaveOk(false); setSaveDialogOpen(true); };
  const doSaveFromDialog = async () => {
    if (!(name || "").trim()) { toast.error("Give your project a name first"); return; }
    const ok = await save();
    if (ok) setSaveOk(true);
  };

  const loadProjectList = async () => {
    setProjLoading(true);
    try { const res = await api.get("/projects"); setProjList(res.data || []); }
    catch { toast.error("Could not load your projects"); }
    finally { setProjLoading(false); }
  };
  const openProject = async (pid) => {
    setProjMenuOpen(false);
    if (pid === projectId) return;
    try {
      const res = await api.get(`/projects/${pid}`);
      applyProjectData(res.data);
      setDirty(false);
      toast.success(`Opened “${res.data.name}”`);
    } catch { toast.error("Could not open that project"); }
  };

  const startRename = (p) => { setProjMenuOpen(false); setProjMenuOpenM(false); setRenameTarget(p); setRenameValue(p.name); };
  const confirmRename = async () => {
    const nm = (renameValue || "").trim();
    if (!nm) { toast.error("Name is required"); return; }
    try {
      await api.patch(`/projects/${renameTarget.id}/rename`, { name: nm });
      if (renameTarget.id === projectId) { skipDirty.current = true; setName(nm); }
      setRenameTarget(null);
      loadProjectList();
      toast.success("Project renamed");
    } catch { toast.error("Rename failed"); }
  };
  const duplicateProject = async (p) => {
    try {
      const res = await api.post(`/projects/${p.id}/duplicate`);
      loadProjectList();
      toast.success(`Duplicated as “${res.data.name}”`);
    } catch { toast.error("Duplicate failed"); }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      if (deleteTarget.id === projectId) { setProjectId(null); skipDirty.current = true; setName("Untitled Project"); }
      setDeleteTarget(null);
      loadProjectList();
      toast.success("Project deleted");
    } catch { toast.error("Delete failed"); }
  };

  const renderProjectRow = (p) => (
    <div key={p.id} data-testid={`project-item-${p.id}`} className="flex items-center gap-2 px-1.5 py-1.5 rounded-md hover:bg-[#38BDF8]/10 group">
      <button onClick={() => openProject(p.id)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`project-open-${p.id}`}>
        <MiniPlan blueprint={p.blueprint} />
        <span className="flex flex-col min-w-0">
          <span className="truncate text-sm text-[#F0F6FC]">{p.name}</span>
          <span className="text-[10px] text-[#6E7681] font-mono-cad truncate">{p.city && p.city !== "Custom" ? p.city : `${((p.blueprint?.floors?.[0]?.rooms) || p.blueprint?.rooms || []).length} rooms`}</span>
        </span>
        {p.id === projectId && <CheckCircle2 className="w-3.5 h-3.5 text-[#34D399] shrink-0" />}
      </button>
      <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
        <button onClick={(e) => { e.stopPropagation(); startRename(p); }} data-testid={`project-rename-${p.id}`} title="Rename" className="p-1 rounded hover:bg-[#30363D] text-[#8B949E] hover:text-[#38BDF8]"><Pencil className="w-3.5 h-3.5" /></button>
        <button onClick={(e) => { e.stopPropagation(); duplicateProject(p); }} data-testid={`project-duplicate-${p.id}`} title="Duplicate" className="p-1 rounded hover:bg-[#30363D] text-[#8B949E] hover:text-[#34D399]"><Copy className="w-3.5 h-3.5" /></button>
        <button onClick={(e) => { e.stopPropagation(); setProjMenuOpen(false); setProjMenuOpenM(false); setDeleteTarget(p); }} data-testid={`project-delete-${p.id}`} title="Delete" className="p-1 rounded hover:bg-[#30363D] text-[#8B949E] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );

  const clearFloor = () => {
    setBlueprint((f) => ({ name: f.name, level: f.level, rooms: [], items: [], walls: [] }));
    setSelectedId(null);
    setConfirmAction(null);
    toast.success(`Cleared "${_activeFloor.name || "floor"}"`);
  };

  const startOver = () => {
    skipDirty.current = true;
    setFloors([{ name: "Ground Floor", rooms: [], items: [] }]);
    setActiveFloor(0);
    setSelectedId(null);
    setProjectId(null);
    setName("Untitled Project");
    setCity("Custom");
    setUsState("");
    setCodeResearch(null);
    setLot(null);
    setLotAddress("");
    setSite({ slope_type: "Flat", slope_pct: 0 });
    setRoofStyle("Gable");
    setFloorHeight(10);
    setMode("site");
    setShowSiteMap(true);
    setRenderPath(null);
    setVideoPath(null);
    setTourClips([]);
    setVideoShots([]);
    setSourcePhotoPath(null);
    setDirty(false);
    setLastSaved(null);
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
    setConfirmAction(null);
    toast.success("Started a fresh new design");
  };

  const doShare = async () => {
    setSharing(true);
    try {
      let pid = projectId;
      const payload = { name, city, state: usState, code_research: codeResearch, blueprint: persistBlueprint(), render_path: renderPath, video_path: videoPath, description: "" };
      if (!pid) {
        const res = await api.post("/projects", payload);
        pid = res.data.id; setProjectId(pid);
      } else {
        await api.put(`/projects/${pid}`, payload);
      }
      const res = await api.post(`/projects/${pid}/share`);
      const url = `${window.location.origin}/share/${res.data.share_id}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Read-only share link copied to clipboard");
    } catch { toast.error("Could not create share link"); }
    finally { setSharing(false); }
  };

  const doCopyBundle = async () => {
    setSharing(true);
    try {
      let pid = projectId;
      const payload = { name, city, state: usState, code_research: codeResearch, blueprint: persistBlueprint(), render_path: renderPath, video_path: videoPath, description: "" };
      if (!pid) {
        const res = await api.post("/projects", payload);
        pid = res.data.id; setProjectId(pid);
      } else {
        await api.put(`/projects/${pid}`, payload);
      }
      const res = await api.post(`/projects/${pid}/share`);
      const url = `${window.location.origin}/bundle/${res.data.share_id}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Buyer bundle link copied \u2014 hand it out at open houses");
    } catch { toast.error("Could not create bundle link"); }
    finally { setSharing(false); }
  };

  const applyAI = (bp, meta) => {
    setBlueprint((f) => ({ ...f, rooms: bp.rooms, items: bp.items }));
    setName(bp.name);
    setRenderPath(meta?.render_path || null);
    if (meta?.source_photo_path) setSourcePhotoPath(meta.source_photo_path);
    setSelectedId(null);
    setTool("select");
  };

  const applyFloors = (newFloors, meta, siteInfo) => {
    setFloors(newFloors);
    setActiveFloor(0);
    if (meta?.name) setName(meta.name);
    if (meta?.source_photo_path) setSourcePhotoPath(meta.source_photo_path);
    setSelectedId(null);
    setTool("select");
  };

  const furnishAI = (style = "cozy") => {
    const furn = autoFurnish(blueprint.rooms, style);
    const keepItems = blueprint.items.filter((it) => it.layer !== "furniture");
    setBlueprint((f) => ({ ...f, items: [...f.items.filter((it) => it.layer !== "furniture"), ...furn] }));
    setActiveLayers((l) => ({ ...l, furniture: true }));
    const { conflicts } = checkClearance(blueprint.rooms, [...keepItems, ...furn]);
    const styleLabel = FURNISH_STYLES[style]?.label || "";
    toast.success(`Auto-furnished ${blueprint.rooms.length} room(s) · ${styleLabel} · ${furn.length} pieces`);
    if (conflicts.length) {
      toast.warning(`${conflicts.length} piece(s) block a doorway/window — highlighted in red`, { duration: 6000 });
    }
  };

  const autoFixClearance = () => {
    const { items, moved } = nudgeClear(blueprint.rooms, blueprint.items);
    if (!moved) { toast.info("Nothing to move — no clearer spot found"); return; }
    setBlueprint((f) => ({ ...f, items }));
    toast.success(`Nudged ${moved} piece(s) clear of doorways/windows`);
  };

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setBlueprint((b) => ({ ...b, rooms: b.rooms.filter((r) => r.id !== selectedId), items: b.items.filter((i) => i.id !== selectedId), walls: (b.walls || []).filter((w) => w.id !== selectedId), runs: (b.runs || []).filter((r) => r.id !== selectedId) }));
    setSelectedId(null);
  }, [selectedId]);

  const selRoom = blueprint.rooms.find((r) => r.id === selectedId);
  const selItem = blueprint.items.find((i) => i.id === selectedId);
  const selWall = (blueprint.walls || []).find((w) => w.id === selectedId);
  const selRun = (blueprint.runs || []).find((r) => r.id === selectedId);

  const updRoom = (patch) => setBlueprint((b) => ({ ...b, rooms: b.rooms.map((r) => r.id === selectedId ? { ...r, ...patch } : r) }));
  const updItem = (patch) => setBlueprint((b) => ({ ...b, items: b.items.map((i) => i.id === selectedId ? { ...i, ...patch } : i) }));
  const updWall = (patch) => setBlueprint((b) => ({ ...b, walls: (b.walls || []).map((w) => w.id === selectedId ? { ...w, ...patch } : w) }));
  const splitWall = () => {
    const w = selWall;
    if (!w) return;
    const mx = (w.x1 + w.x2) / 2, my = (w.y1 + w.y2) / 2;
    const a = { id: uid(), x1: w.x1, y1: w.y1, x2: mx, y2: my, thickness: w.thickness };
    const b2 = { id: uid(), x1: mx, y1: my, x2: w.x2, y2: w.y2, thickness: w.thickness };
    setBlueprint((b) => ({ ...b, walls: [...(b.walls || []).filter((x) => x.id !== w.id), a, b2] }));
    setSelectedId(a.id);
    toast.success("Wall split into two segments");
  };

  const toggleLayer = (l) => setActiveLayers((s) => ({ ...s, [l]: !s[l] }));

  const downloadConstructionSet = async () => {
    if (!projectId) { toast.error("Save the project first, then export"); return; }
    setConLoading(true);
    try {
      const res = await api.post(`/projects/${projectId}/construction-set`);
      const blob = await api.get(`/files/${res.data.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(blob.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(name || "project").replace(/\s+/g, "_")}-construction-set.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Construction set PDF downloaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    finally { setConLoading(false); }
  };

  const footprintBox = () => {
    const gf = floors.find((f) => (f.level || "floor") === "floor") || floors[activeFloor] || {};
    const rooms = gf.rooms || [];
    if (!rooms.length) return { x: 20, y: 16, w: 40, h: 30 };
    const x0 = Math.min(...rooms.map((r) => r.x)), y0 = Math.min(...rooms.map((r) => r.y));
    const x1 = Math.max(...rooms.map((r) => r.x + r.w)), y1 = Math.max(...rooms.map((r) => r.y + r.h));
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  };
  const perimeterWalls = (b, thickness = 0.8) => ([
    { id: uid(), x1: b.x, y1: b.y, x2: b.x + b.w, y2: b.y, thickness },
    { id: uid(), x1: b.x + b.w, y1: b.y, x2: b.x + b.w, y2: b.y + b.h, thickness },
    { id: uid(), x1: b.x + b.w, y1: b.y + b.h, x2: b.x, y2: b.y + b.h, thickness },
    { id: uid(), x1: b.x, y1: b.y + b.h, x2: b.x, y2: b.y, thickness },
  ]);
  const addFoundation = () => {
    if (floors.some((f) => f.level === "foundation")) { toast.info("Foundation level already added"); return; }
    const b = footprintBox();
    const items = [[b.x, b.y], [b.x + b.w, b.y], [b.x, b.y + b.h], [b.x + b.w, b.y + b.h], [b.x + b.w / 2, b.y], [b.x + b.w / 2, b.y + b.h], [b.x, b.y + b.h / 2], [b.x + b.w, b.y + b.h / 2]]
      .map(([x, y]) => ({ id: uid(), kind: "footing", layer: "engineering", x, y }));
    const nf = { name: "Foundation", level: "foundation", rooms: [{ id: uid(), name: "Slab (Foundation)", x: b.x, y: b.y, w: b.w, h: b.h }], items, walls: perimeterWalls(b, 0.9) };
    setFloors((fs) => [nf, ...fs]); setActiveFloor(0); setSelectedId(null);
    toast.success("Foundation level added below grade (footings + slab + stem walls)");
  };
  const addBasement = () => {
    if (floors.some((f) => f.level === "basement")) { toast.info("Basement level already added"); return; }
    const b = footprintBox();
    const nf = { name: "Basement", level: "basement", rooms: [{ id: uid(), name: "Basement", x: b.x, y: b.y, w: b.w, h: b.h }],
      items: [{ id: uid(), kind: "panel", layer: "electrical", x: b.x + 1.5, y: b.y + 1.5 }, { id: uid(), kind: "condenser", layer: "hvac", x: b.x + 4, y: b.y + 1.5 }],
      walls: perimeterWalls(b, 0.8) };
    setFloors((fs) => [nf, ...fs]); setActiveFloor(0); setSelectedId(null);
    toast.success("Basement level added below grade (foundation walls + slab)");
  };

  const suggestFromGrade = () => {
    const slope = site?.slope_type || "Flat";
    const gradePct = Number(site?.slope_pct) || 0;
    const b = footprintBox();
    const elevs = site?.spot_elevations || [];
    let drop;
    if (elevs.length >= 2) { const zs = elevs.map((e) => Number(e.z) || 0); drop = Math.abs(Math.max(...zs) - Math.min(...zs)); }
    else { drop = (b.h * gradePct) / 100; }
    drop = Math.round(drop * 10) / 10;
    const steps = Math.max(1, Math.min(6, Math.round(drop / 2)));
    const walkout = slope !== "Flat" && drop >= 6;
    const area = Math.round(b.w * b.h);
    const totalCy = (area * drop / 2) / 27;
    const cutCy = Math.round(totalCy / 2), fillCy = Math.round(totalCy / 2);
    const ewCost = Math.round(cutCy * 45 + fillCy * 25);
    setGradeSuggestion({ slope, gradePct, drop, steps, walkout, footprint: b, area, cutCy, fillCy, ewCost,
      hasFoundation: floors.some((f) => f.level === "foundation"),
      hasBasement: floors.some((f) => f.level === "basement") });
  };

  const applyGradeSuggestion = () => {
    const { steps, walkout, footprint: b } = gradeSuggestion;
    const newFloors = [];
    if (!floors.some((f) => f.level === "foundation")) {
      const items = [];
      const rows = steps + 1;
      for (let s = 0; s < rows; s++) {
        const y = Math.round(b.y + b.h * (rows > 1 ? s / (rows - 1) : 0));
        [b.x, b.x + b.w / 2, b.x + b.w].forEach((x) => items.push({ id: uid(), kind: "footing", layer: "engineering", x: Math.round(x), y }));
      }
      items.push({ id: uid(), kind: "retaining", layer: "engineering", x: Math.round(b.x + b.w / 2), y: b.y });
      const nf = { name: "Stepped Foundation", level: "foundation",
        rooms: [{ id: uid(), name: `Stepped Slab · ${steps} step${steps > 1 ? "s" : ""}`, x: b.x, y: b.y, w: b.w, h: b.h }],
        items, walls: perimeterWalls(b, 0.9) };
      newFloors.push(nf);
    }
    if (walkout && !floors.some((f) => f.level === "basement")) {
      const nf = { name: "Walk-Out Basement", level: "basement",
        rooms: [{ id: uid(), name: "Walk-Out Basement", x: b.x, y: b.y, w: b.w, h: b.h }],
        items: [
          { id: uid(), kind: "door", layer: "structural", x: Math.round(b.x + b.w / 2), y: b.y + b.h },
          { id: uid(), kind: "window", layer: "structural", x: Math.round(b.x + b.w * 0.25), y: b.y + b.h },
          { id: uid(), kind: "window", layer: "structural", x: Math.round(b.x + b.w * 0.75), y: b.y + b.h },
          { id: uid(), kind: "panel", layer: "electrical", x: b.x + 1.5, y: b.y + 1.5 },
        ],
        walls: perimeterWalls(b, 0.8) };
      newFloors.push(nf);
    }
    if (!newFloors.length) { toast.info("Foundation and basement are already added"); setGradeSuggestion(null); return; }
    setFloors((fs) => [...newFloors, ...fs]); setActiveFloor(0); setSelectedId(null);
    const drop = gradeSuggestion.drop;
    setGradeSuggestion(null);
    if (mode === "site") setMode("blueprint");
    toast.success(`Added ${newFloors.map((f) => f.name).join(" + ")} for ~${drop} ft of grade fall`);
  };

  const buildableEnvelope = () => {
    if (site?.boundary?.length >= 3) {
      const ins = insetPolygon(site.boundary, Math.max(effSetbacks?.side || 6, effSetbacks?.front || 10, 3));
      const xs = ins.map((p) => p.x), ys = ins.map((p) => p.y);
      return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
    }
    const L = effLot;
    if (effSetbacks) return { x: L.x + (effSetbacks.side || 0), y: L.y + (effSetbacks.front || 0), w: Math.max(10, L.w - 2 * (effSetbacks.side || 0)), h: Math.max(10, L.h - (effSetbacks.front || 0) - (effSetbacks.rear || 0)) };
    return { x: L.x, y: L.y, w: L.w, h: L.h };
  };

  const autoFitHouse = () => {
    if (blueprint.rooms.length > 0) { toast.info("Clear the floor first to auto-fit a fresh footprint"); return; }
    const env = buildableEnvelope();
    const w = Math.max(12, Math.round(env.w));
    const h = Math.max(12, Math.round(Math.min(env.h, 60)));
    const x = Math.round(env.x), y = Math.round(env.y);
    const cols = w >= 24 ? 2 : 1, rows = h >= 24 ? 2 : 1;
    const rW = w / cols, rH = h / rows;
    const names = ["Living", "Kitchen", "Bedroom", "Bath"];
    const rooms = []; let k = 0;
    for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
      rooms.push({ id: uid(), name: names[k++] || "Room", x: Math.round(x + i * rW), y: Math.round(y + j * rH), w: Math.round(rW), h: Math.round(rH) });
    }
    setBlueprint((b) => ({ ...b, rooms: [...b.rooms, ...rooms] }));
    if (mode === "site") setMode("blueprint");
    setSelectedId(null);
    toast.success(`Auto-fit a ${w}×${h} ft starter footprint inside the buildable area`);
  };

  const exportZoningReport = () => {
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const L = effLot;
    const lotArea = site?.boundary?.length >= 3 ? Math.round(polygonArea(site.boundary)) : Math.round(L.w * L.h);
    const env = buildableEnvelope();
    const buildableArea = Math.round(env.w * env.h);
    const footprint = totalArea(blueprint.rooms);
    const cov = lotArea ? (footprint / lotArea * 100) : 0;
    const drop = (site?.spot_elevations || []).length >= 2
      ? Math.abs(Math.max(...site.spot_elevations.map((e) => +e.z || 0)) - Math.min(...site.spot_elevations.map((e) => +e.z || 0)))
      : Math.round((footprintBox().h * (Number(site?.slope_pct) || 0) / 100) * 10) / 10;
    const totalCy = (footprint * drop / 2) / 27;
    const cutCy = Math.round(totalCy / 2);
    const sb = effSetbacks || {};
    let y = 56;
    pdf.setFontSize(18); pdf.setTextColor(20); pdf.text("Site & Zoning Report", 40, y); y += 8;
    pdf.setDrawColor(220); pdf.line(40, y, pw - 40, y); y += 22;
    pdf.setFontSize(11); pdf.setTextColor(90);
    pdf.text(`Project: ${name}`, 40, y); y += 16;
    pdf.text(`Address / City: ${lotAddress || city || "—"}`, 40, y); y += 16;
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 40, y); y += 24;
    const row = (label, val) => {
      pdf.setTextColor(20); pdf.setFontSize(11); pdf.text(label, 48, y);
      pdf.setTextColor(20, 100, 200); pdf.text(String(val), 320, y);
      pdf.setTextColor(90); y += 20;
    };
    const section = (t) => { pdf.setFontSize(12); pdf.setTextColor(20, 100, 200); pdf.text(t, 40, y); y += 6; pdf.setDrawColor(230); pdf.line(40, y, pw - 40, y); y += 16; pdf.setTextColor(90); };
    section("Lot");
    row("Lot area", `${lotArea.toLocaleString()} sf  (${(lotArea / 43560).toFixed(2)} ac)`);
    row("Lot dimensions", `${Math.round(L.w)} ft × ${Math.round(L.h)} ft`);
    row("Boundary", site?.boundary?.length >= 3 ? `${site.boundary.length}-sided surveyed polygon` : "Rectangular (estimated)");
    y += 6; section("Setbacks & Buildable Area");
    row("Front setback", `${sb.front || 0} ft`);
    row("Side setback", `${sb.side || 0} ft`);
    row("Rear setback", `${sb.rear || 0} ft`);
    row("Buildable area", `${buildableArea.toLocaleString()} sf`);
    y += 6; section("Coverage");
    row("Building footprint", `${footprint.toLocaleString()} sf`);
    row("Lot coverage", `${cov.toFixed(1)} %`);
    row("Floors", `${floors.length} (${floors.map((f) => f.name).join(", ")})`);
    y += 6; section("Grade & Earthwork");
    row("Slope", `${site?.slope_type || "Flat"} · ${site?.slope_pct || 0}% grade`);
    row("Estimated grade fall", `${drop} ft across footprint`);
    row("Cut / Fill (balanced)", `${cutCy.toLocaleString()} cy each`);
    row("Est. earthwork cost", `$${Math.round(cutCy * 45 + cutCy * 25).toLocaleString()}`);
    pdf.setFontSize(8); pdf.setTextColor(150);
    pdf.text("Estimates from plan geometry — confirm with a licensed surveyor/engineer before permit submittal.", 40, pdf.internal.pageSize.getHeight() - 40);
    pdf.save(`${(name || "site").replace(/\s+/g, "-").toLowerCase()}-zoning-report.pdf`);
    toast.success("Zoning report PDF downloaded");
  };

  const runAssistantTool = async (tool, params = {}) => {
    if (tool === "research_codes") {
      const st = params.state || usState;
      const ct = params.city || (city === "Custom" ? "" : city);
      const res = await api.post("/ai/research-codes", { state: st, city: ct });
      setCodeResearch(res.data);
      if (st) setUsState(st);
      if (ct) setCity(ct);
      return `Researched codes for ${res.data.jurisdiction || [ct, st].filter(Boolean).join(", ") || "your area"}`;
    }
    if (tool === "furnish") {
      const style = FURNISH_STYLES[params.style] ? params.style : "cozy";
      furnishAI(style);
      return `Auto-furnished (${FURNISH_STYLES[style]?.label || style})`;
    }
    if (tool === "estimate_cost") {
      const res = await api.post("/estimate/cost", {
        blueprint, tier: site?.cost_tier || "Standard", region_multiplier: 1.0, stories: floors.length,
        slope_type: site?.slope_type || "Flat", slope_pct: site?.slope_pct || 0, floors, lot: effLot,
      });
      setCostOpen(true);
      return `Est. build ~$${(res.data.total || 0).toLocaleString()} · materials ~$${(res.data.materials_total || 0).toLocaleString()}`;
    }
    return null;
  };

  const estimateLot = async () => {
    if (!lotAddress.trim()) { toast.error("Enter a street address first"); return; }
    setLotEstimating(true);
    try {
      let used = false;
      try {
        const p = await api.post("/parcels/lookup", { address: lotAddress });
        if (p.data?.width_ft && p.data?.depth_ft) {
          setLot({ x: effLot.x, y: effLot.y, w: Number(p.data.width_ft), h: Number(p.data.depth_ft) });
          const sq = p.data.sqft ? ` · ${Number(p.data.sqft).toLocaleString()} ft²` : "";
          toast.success(`Recorded parcel: ${Math.round(p.data.width_ft)} × ${Math.round(p.data.depth_ft)} ft${sq}`);
          used = true;
        }
      } catch (e) {
        if (e.response?.status === 404) toast.info("No recorded parcel found — using AI estimate");
      }
      if (!used) {
        const res = await api.post("/ai/estimate-lot", { address: lotAddress });
        const w = Number(res.data.width_ft) || 60;
        const h = Number(res.data.depth_ft) || 100;
        setLot({ x: effLot.x, y: effLot.y, w, h });
        toast.success(res.data.notes || `AI estimate: ${Math.round(w)} × ${Math.round(h)} ft`);
      }
      setLotDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch the lot");
    } finally {
      setLotEstimating(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#0D1117] text-[#F0F6FC] overflow-hidden">
      {/* Header */}
      <header className="h-14 shrink-0 glass border-b border-[#30363D] flex items-center px-4 gap-3 z-30">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-brand-logo">
          <Ruler className="w-5 h-5 text-[#38BDF8]" />
          <span className="font-heading font-bold hidden sm:block">ArchiMesh<span className="text-[#38BDF8]">AI</span></span>
        </Link>
        <div className="h-6 w-px bg-[#30363D] hidden sm:block" />
        <div className="relative flex items-center" title="Click to rename your project">
          <PenLine className="w-3.5 h-3.5 text-[#6E7681] absolute left-2.5 pointer-events-none" />
          <Input value={name} onChange={(e) => setName(e.target.value)} onFocus={(e) => e.target.select()} placeholder="Untitled Project" data-testid="project-name-input"
            className="max-w-[220px] h-9 pl-8 bg-[#0D1117] border-[#30363D] font-heading font-semibold hover:border-[#38BDF8]/60 focus:border-[#38BDF8]" />
        </div>
        <div className="hidden md:flex items-center gap-2 flex-1 min-w-0 overflow-x-auto toolbar-scroll">
          <Button size="sm" variant="outline" onClick={() => setAiOpen(true)} data-testid="nav-ai-generate-btn" className="border-[#30363D] bg-transparent hover:bg-[#A855F7]/10 hover:text-[#A855F7] hover:border-[#A855F7] shrink-0">
            <Wand2 className="w-4 h-4 mr-1.5" /> AI Design
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAssistantOpen((v) => !v)} data-testid="nav-assistant-btn" className={`border-[#30363D] ${assistantOpen ? "bg-[#A855F7] text-white border-[#A855F7]" : "bg-transparent hover:bg-[#A855F7]/10 hover:text-[#A855F7] hover:border-[#A855F7]"}`}>
            <Sparkles className="w-4 h-4 mr-1.5" /> Assistant
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPhotoOpen(true)} data-testid="nav-photo-upload-btn" className="border-[#30363D] bg-transparent hover:bg-[#34D399]/10 hover:text-[#34D399] hover:border-[#34D399]">
            <ScanLine className="w-4 h-4 mr-1.5" /> Photo
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSiteOpen(true)} data-testid="nav-site-btn" className="border-[#30363D] bg-transparent hover:bg-[#F43F5E]/10 hover:text-[#F43F5E] hover:border-[#F43F5E]">
            <Mountain className="w-4 h-4 mr-1.5" /> Site
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid="nav-furnish-btn" className="border-[#30363D] bg-transparent hover:bg-[#E879F9]/10 hover:text-[#E879F9] hover:border-[#E879F9]">
                <Sofa className="w-4 h-4 mr-1.5" /> Furnish <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">Furnishing Style</DropdownMenuLabel>
              {Object.entries(FURNISH_STYLES).map(([key, s]) => (
                <DropdownMenuItem key={key} onClick={() => furnishAI(key)} data-testid={`furnish-style-${key}`} className="flex flex-col items-start gap-0 cursor-pointer focus:bg-[#E879F9]/10">
                  <span className="text-sm font-medium text-[#E879F9]">{s.label}</span>
                  <span className="text-[11px] text-[#8B949E]">{s.desc}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="outline" onClick={() => setOrdOpen(true)} data-testid="nav-ordinance-btn" className="border-[#30363D] bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]">
            <Landmark className="w-4 h-4 mr-1.5" /> Ordinance
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCodeOpen(true)} data-testid="nav-codes-btn" className="border-[#30363D] bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]">
            <BookText className="w-4 h-4 mr-1.5" /> Codes AI
          </Button>
          {effSetbacks && (
            <Button size="sm" variant="outline" onClick={() => setShowSetbacks((v) => !v)} data-testid="nav-setback-btn" className={`border-[#30363D] ${showSetbacks ? "bg-[#F59E0B] text-[#0D1117] border-[#F59E0B]" : "bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]"}`}>
              <Ruler className="w-4 h-4 mr-1.5" /> Setbacks
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowShadows((v) => !v)} data-testid="nav-shadows-btn" className={`border-[#30363D] ${showShadows ? "bg-[#FBBF24] text-[#0D1117] border-[#FBBF24]" : "bg-transparent hover:bg-[#FBBF24]/10 hover:text-[#FBBF24] hover:border-[#FBBF24]"}`}>
            <Sun className="w-4 h-4 mr-1.5" /> Shadows
          </Button>
          <Button size="sm" variant="outline" onClick={toggleDimGuard} data-testid="nav-dimguard-btn" title="Flag rooms that drift from their locked plan size" className={`border-[#30363D] ${dimGuard ? "bg-[#38BDF8] text-[#0D1117] border-[#38BDF8]" : "bg-transparent hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] hover:border-[#38BDF8]"}`}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Dim Guard{dimGuard && driftCount > 0 ? ` · ${driftCount}` : ""}
          </Button>
          {dimGuard && (
            <Button size="sm" variant="outline" onClick={() => { lockDimTargets(); toast.success("Re-locked current sizes as the new targets"); }} data-testid="dimguard-relock-btn" className="border-[#30363D] bg-transparent hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] hover:border-[#38BDF8]">
              <Ruler className="w-4 h-4 mr-1.5" /> Re-lock sizes
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setCostOpen(true)} data-testid="nav-cost-btn" className="border-[#30363D] bg-transparent hover:bg-[#34D399]/10 hover:text-[#34D399] hover:border-[#34D399]">
            <Calculator className="w-4 h-4 mr-1.5" /> Cost
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setRenderAuto(true); setRenderOpen(true); }} data-testid="nav-exterior-render-btn" className="border-[#30363D] bg-transparent hover:bg-[#34D399]/10 hover:text-[#34D399] hover:border-[#34D399]">
            <Home className="w-4 h-4 mr-1.5" /> Exterior
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setRenderAuto(false); setRenderOpen(true); }} data-testid="nav-render-btn" className="border-[#30363D] bg-transparent hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] hover:border-[#38BDF8]">
            <ImageIcon className="w-4 h-4 mr-1.5" /> 3D Render
          </Button>
          <Button size="sm" variant="outline" onClick={downloadConstructionSet} disabled={conLoading} data-testid="nav-conset-btn" className="border-[#30363D] bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]">
            {conLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Construction Set
          </Button>
          <Button size="sm" variant="outline" onClick={() => setWaOpen(true)} data-testid="nav-3d-btn" className="border-[#30363D] bg-transparent hover:bg-[#A855F7]/10 hover:text-[#A855F7] hover:border-[#A855F7]">
            <Box className="w-4 h-4 mr-1.5" /> 3D View
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSectionOpen(true)} data-testid="nav-section-btn" className="border-[#30363D] bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]">
            <Scissors className="w-4 h-4 mr-1.5" /> Section
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSolarOpen(true)} data-testid="nav-solar-btn" className="border-[#30363D] bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]">
            <Sun className="w-4 h-4 mr-1.5" /> Solar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setHeatmap((v) => !v)} data-testid="nav-heatmap-btn" className={`border-[#30363D] ${heatmap ? "bg-[#F59E0B] text-[#0D1117] border-[#F59E0B]" : "bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]"}`}>
            <Sun className="w-4 h-4 mr-1.5" /> Heatmap
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSunPath((v) => !v)} data-testid="nav-sunpath-btn" className={`border-[#30363D] ${sunPath ? "bg-[#F59E0B] text-[#0D1117] border-[#F59E0B]" : "bg-transparent hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] hover:border-[#F59E0B]"}`}>
            <Sunrise className="w-4 h-4 mr-1.5" /> Sun Path
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSplitView((v) => !v)} data-testid="nav-liveview-btn" className={`border-[#30363D] ${splitView ? "bg-[#38BDF8] text-[#0D1117] border-[#38BDF8]" : "bg-transparent hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] hover:border-[#38BDF8]"}`}>
            <Columns2 className="w-4 h-4 mr-1.5" /> Live View
          </Button>
          <Button size="sm" variant="outline" onClick={() => setExportOpen(true)} data-testid="nav-export-blueprint-btn" className="border-[#30363D] bg-transparent hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] hover:border-[#38BDF8]">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <Button size="sm" variant="outline" onClick={doShare} disabled={sharing} data-testid="nav-share-btn" className="border-[#30363D] bg-transparent hover:bg-[#34D399]/10 hover:text-[#34D399] hover:border-[#34D399]">
            {sharing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Share2 className="w-4 h-4 mr-1.5" />} Share
          </Button>
          <Button size="sm" variant="outline" onClick={doCopyBundle} disabled={sharing} data-testid="nav-copy-bundle-btn" className="border-[#30363D] bg-transparent hover:bg-[#A855F7]/10 hover:text-[#A855F7] hover:border-[#A855F7]">
            <Copy className="w-4 h-4 mr-1.5" /> Copy buyer link
          </Button>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-auto md:ml-2">
          <span className="hidden lg:flex items-center gap-1.5 text-xs font-mono-cad" data-testid="save-status">
            {dirty
              ? <><span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" /><span className="text-[#F59E0B]">Unsaved changes</span></>
              : <><CheckCircle2 className="w-3.5 h-3.5 text-[#34D399]" /><span className="text-[#8B949E]">{lastSaved ? "All changes saved" : "Saved"}</span></>}
          </span>
          <DropdownMenu open={projMenuOpen} onOpenChange={(o) => { setProjMenuOpen(o); if (o) loadProjectList(); }}>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid="projects-menu-btn" className="border-[#30363D] bg-transparent hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] hover:border-[#38BDF8]">
                <FolderOpen className="w-4 h-4 mr-1.5" /> Projects <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#161B22] border-[#30363D] text-[#F0F6FC] w-72 max-h-[70vh] overflow-y-auto" data-testid="projects-menu">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">Recall a saved project</DropdownMenuLabel>
              {projLoading && <div className="px-2 py-3 text-xs text-[#8B949E] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
              {!projLoading && projList.length === 0 && <div className="px-2 py-3 text-xs text-[#6E7681]">No saved projects yet — hit Save to create one.</div>}
              {!projLoading && projList.map((p) => renderProjectRow(p))}
              <DropdownMenuSeparator className="bg-[#30363D]" />
              <DropdownMenuItem onClick={() => { setProjMenuOpen(false); setConfirmAction("all"); }} data-testid="project-new-item" className="cursor-pointer text-[#34D399] focus:bg-[#34D399]/10"><FilePlus2 className="w-3.5 h-3.5 mr-1.5" /> New project</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="outline" onClick={() => setConfirmAction("all")} data-testid="new-project-btn" className="border-[#30363D] bg-transparent hover:bg-[#EF4444]/10 hover:text-[#EF4444] hover:border-[#EF4444]">
            <FilePlus2 className="w-4 h-4 mr-1.5" /> New
          </Button>
          <Button onClick={openSaveDialog} disabled={saving} data-testid="save-project-btn" className={`font-semibold px-5 ${dirty ? "bg-[#F59E0B] hover:bg-[#D97706] text-[#0D1117]" : "bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117]"}`}>
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} {dirty ? "Save changes" : "Save"}
          </Button>
        </div>
        <Link to="/dashboard"><Button size="icon" variant="ghost" className="text-[#8B949E]" data-testid="nav-dashboard-btn"><LayoutGrid className="w-4 h-4" /></Button></Link>
      </header>

      {/* mobile action bar */}
      <div className="md:hidden flex items-center gap-2 px-3 py-2 border-b border-[#30363D] bg-[#161B22] overflow-x-auto shrink-0">
        <Button size="sm" onClick={openSaveDialog} disabled={saving} data-testid="save-project-btn-mobile" className={`font-semibold shrink-0 ${dirty ? "bg-[#F59E0B] text-[#0D1117]" : "bg-[#38BDF8] text-[#0D1117]"}`}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}{dirty ? "Save*" : "Save"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setConfirmAction("all")} data-testid="new-project-btn-mobile" className="text-[#EF4444] shrink-0"><FilePlus2 className="w-4 h-4 mr-1" />New</Button>
        <DropdownMenu open={projMenuOpenM} onOpenChange={(o) => { setProjMenuOpenM(o); if (o) loadProjectList(); }}>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" data-testid="projects-menu-btn-mobile" className="text-[#38BDF8] shrink-0"><FolderOpen className="w-4 h-4 mr-1" />Projects</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-[#161B22] border-[#30363D] text-[#F0F6FC] w-64 max-h-[60vh] overflow-y-auto">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">Recall a saved project</DropdownMenuLabel>
            {projLoading && <div className="px-2 py-3 text-xs text-[#8B949E] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
            {!projLoading && projList.length === 0 && <div className="px-2 py-3 text-xs text-[#6E7681]">No saved projects yet.</div>}
            {!projLoading && projList.map((p) => renderProjectRow(p))}
            <DropdownMenuSeparator className="bg-[#30363D]" />
            <DropdownMenuItem onClick={() => { setProjMenuOpen(false); setConfirmAction("all"); }} className="cursor-pointer text-[#34D399] focus:bg-[#34D399]/10"><FilePlus2 className="w-3.5 h-3.5 mr-1.5" /> New project</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button size="sm" variant="ghost" onClick={() => setAiOpen(true)} className="text-[#A855F7]"><Wand2 className="w-4 h-4 mr-1" />AI</Button>
        <Button size="sm" variant="ghost" onClick={() => setAssistantOpen((v) => !v)} className="text-[#A855F7]"><Sparkles className="w-4 h-4 mr-1" />Assistant</Button>
        <Button size="sm" variant="ghost" onClick={() => setPhotoOpen(true)} className="text-[#34D399]"><ScanLine className="w-4 h-4 mr-1" />Photo</Button>
        <Button size="sm" variant="ghost" onClick={() => setSiteOpen(true)} className="text-[#F43F5E]"><Mountain className="w-4 h-4 mr-1" />Site</Button>
        <Button size="sm" variant="ghost" onClick={() => furnishAI("cozy")} className="text-[#E879F9]"><Sofa className="w-4 h-4 mr-1" />Furnish</Button>
        <Button size="sm" variant="ghost" onClick={() => setOrdOpen(true)} className="text-[#F59E0B]"><Landmark className="w-4 h-4 mr-1" />Codes</Button>
        <Button size="sm" variant="ghost" onClick={() => setCostOpen(true)} className="text-[#34D399]"><Calculator className="w-4 h-4 mr-1" />Cost</Button>
        <Button size="sm" variant="ghost" onClick={() => setSplitView((v) => !v)} className="text-[#38BDF8]"><Columns2 className="w-4 h-4 mr-1" />Split</Button>
        <Button size="sm" variant="ghost" onClick={() => { setRenderAuto(true); setRenderOpen(true); }} className="text-[#34D399]"><Home className="w-4 h-4 mr-1" />Exterior</Button>
        <Button size="sm" variant="ghost" onClick={() => { setRenderAuto(false); setRenderOpen(true); }} className="text-[#38BDF8]"><ImageIcon className="w-4 h-4 mr-1" />Render</Button>
        <Button size="sm" variant="ghost" onClick={() => setWaOpen(true)} className="text-[#A855F7]"><Box className="w-4 h-4 mr-1" />3D</Button>
        <Button size="sm" variant="ghost" onClick={() => setSectionOpen(true)} className="text-[#F59E0B]"><Scissors className="w-4 h-4 mr-1" />Section</Button>
        <Button size="sm" variant="ghost" onClick={() => setSolarOpen(true)} className="text-[#F59E0B]"><Sun className="w-4 h-4 mr-1" />Solar</Button>
        <Button size="sm" variant="ghost" onClick={() => setExportOpen(true)} className="text-[#38BDF8]"><Download className="w-4 h-4 mr-1" />Export</Button>
        <Button size="sm" variant="ghost" onClick={doShare} className="text-[#34D399]"><Share2 className="w-4 h-4 mr-1" />Share</Button>
      </div>

      {/* Mode tabs: Site Map -> Blueprint */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-[#30363D] bg-[#161B22] shrink-0" data-testid="mode-tabs">
        <button onClick={() => setMode("site")} data-testid="mode-tab-site"
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${mode === "site" ? "bg-[#F43F5E] text-white" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
          <Map className="w-3.5 h-3.5" /> 1 · Site Map
        </button>
        <ChevronDown className="w-3.5 h-3.5 text-[#484F58] -rotate-90" />
        <button onClick={() => setMode("blueprint")} data-testid="mode-tab-blueprint"
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${mode === "blueprint" ? "bg-[#38BDF8] text-[#0D1117]" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
          <LayoutGrid className="w-3.5 h-3.5" /> 2 · Blueprint
        </button>
        {mode === "site" && (
          <span className="ml-auto text-[11px] text-[#6E7681] font-mono-cad hidden sm:block">Set up the land & elevation first, then draw the house</span>
        )}
      </div>

      {/* Floor bar */}
      {mode === "blueprint" && (
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-[#30363D] bg-[#0D1117] shrink-0 overflow-x-auto" data-testid="floor-bar">
        <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad mr-1"><Layers className="w-3 h-3" /> Floors</span>
        {floors.map((f, i) => (
          <button key={i} onClick={() => { setActiveFloor(i); setSelectedId(null); }} data-testid={`floor-tab-${i}`}
            className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${i === activeFloor ? "bg-[#38BDF8] text-[#0D1117]" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
            {f.name}
          </button>
        ))}
        <button onClick={addFloor} data-testid="add-floor-btn" className="px-2 py-1 rounded-md text-[#34D399] hover:bg-[#34D399]/10 flex items-center gap-1 text-xs"><Plus className="w-3.5 h-3.5" /> Storey</button>
        <button onClick={addFoundation} data-testid="add-foundation-btn" className="px-2 py-1 rounded-md text-[#F59E0B] hover:bg-[#F59E0B]/10 flex items-center gap-1 text-xs"><Plus className="w-3.5 h-3.5" /> Foundation</button>
        <button onClick={addBasement} data-testid="add-basement-btn" className="px-2 py-1 rounded-md text-[#A855F7] hover:bg-[#A855F7]/10 flex items-center gap-1 text-xs"><Plus className="w-3.5 h-3.5" /> Basement</button>
        <button onClick={suggestFromGrade} data-testid="grade-foundation-btn" className="px-2 py-1 rounded-md text-[#F43F5E] hover:bg-[#F43F5E]/10 flex items-center gap-1 text-xs" title="Suggest stepped footings & a walk-out basement from the site grade"><Mountain className="w-3.5 h-3.5" /> Grade → Foundation</button>
        <button onClick={autoFitHouse} data-testid="autofit-floorbar-btn" className="px-2 py-1 rounded-md text-[#38BDF8] hover:bg-[#38BDF8]/10 flex items-center gap-1 text-xs" title="Auto-place a starter footprint inside the buildable area"><Sparkles className="w-3.5 h-3.5" /> Auto-Fit</button>
        <button onClick={exportZoningReport} data-testid="zoning-report-btn" className="px-2 py-1 rounded-md text-[#34D399] hover:bg-[#34D399]/10 flex items-center gap-1 text-xs" title="Export a one-page site & zoning report PDF"><BookText className="w-3.5 h-3.5" /> Zoning PDF</button>
        {floors.length > 1 && (
          <button onClick={deleteFloor} data-testid="delete-floor-btn" className="px-2 py-1 rounded-md text-[#6E7681] hover:text-[#EF4444]" title="Delete this floor"><Trash2 className="w-3.5 h-3.5" /></button>
        )}
        <button onClick={() => setConfirmAction("floor")} data-testid="clear-floor-btn" className="px-2 py-1 rounded-md text-[#6E7681] hover:text-[#F59E0B] flex items-center gap-1 text-xs" title="Clear everything on this floor"><Eraser className="w-3.5 h-3.5" /> Clear</button>
        {site.slope_type && site.slope_type !== "Flat" && (
          <span className="ml-auto flex items-center gap-1 text-[10px] font-mono-cad text-[#F43F5E] whitespace-nowrap" data-testid="site-badge">
            <Mountain className="w-3 h-3" /> {site.slope_type} · {site.slope_pct}% grade
          </span>
        )}
      </div>
      )}

      <div className="flex-1 flex overflow-hidden relative">
        {mode === "site" ? (
          <SiteMap site={site} setSite={setSite} lot={effLot} setLot={setLot} city={city === "Custom" ? "" : city}
            worldW={worldW} worldH={worldH} onAutoFit={autoFitHouse} onZoningReport={exportZoningReport}
            onContinue={() => { setMode("blueprint"); toast.success("Site saved — now trace your house on the land"); }} />
        ) : (
        <>
        {/* Left toolbar */}
        <aside className="w-14 sm:w-16 shrink-0 border-r border-[#30363D] bg-[#161B22] flex flex-col items-center py-3 gap-1 overflow-y-auto">
          {TOOLS.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} title={t.label} data-testid={`cad-tool-${t.id}`}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${tool === t.id ? "bg-[#38BDF8] text-[#0D1117]" : "text-[#8B949E] hover:bg-[#21262D] hover:text-[#F0F6FC]"}`}>
              <t.icon className="w-5 h-5" />
            </button>
          ))}
          <div className="w-8 h-px bg-[#30363D] my-1" />
          {Object.entries(FIXTURES).map(([layer, cfg]) => (
            <div key={layer} className="flex flex-col items-center gap-1">
              {cfg.kinds.map((k) => (
                <button key={k} onClick={() => setTool(k)} title={ITEM_KINDS[k].title} data-testid={`cad-tool-${k}`}
                  className={`w-10 h-9 rounded-lg flex items-center justify-center text-[9px] font-mono-cad font-bold transition-colors ${tool === k ? "" : "hover:bg-[#21262D]"}`}
                  style={{ color: tool === k ? "#0D1117" : LAYERS[layer].accent, background: tool === k ? LAYERS[layer].accent : "transparent" }}>
                  {ITEM_KINDS[k].label}
                </button>
              ))}
              <div className="w-6 h-px bg-[#30363D] my-0.5" />
            </div>
          ))}
        </aside>

        {/* Canvas */}
        <main className="flex-1 flex overflow-hidden">
          <div className="relative flex-1 overflow-hidden">
          <CanvasGrid blueprint={blueprint} setBlueprint={setBlueprint} tool={tool}
            activeLayers={activeLayers} selectedId={selectedId} setSelectedId={setSelectedId} pxPerFt={pxPerFt}
            heatmap={heatmap} solarScores={solar.byId} solarHours={solarHours} sunPath={sunPath} season={season} blockedItems={clearance.blockedIds}
            showSetbacks={showSetbacks} setbacks={effSetbacks} lot={effLot} setLot={setLot}
            siteMapUrl={site?.image_path && showSiteMap ? fileUrl(site.image_path) : null} siteMapLot={effLot}
            worldW={worldW} worldH={worldH} boundary={site?.boundary || null}
            showShadows={showShadows} shadow={shadow} driveway={site?.driveway || null}
            topOfSlope={site?.top_of_slope || null} downhillZone={site?.downhill_zone || null} cantilever={site?.cantilever || null} dimGuard={dimGuard} />

          {/* Lot + Coverage meter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 glass rounded-xl border border-[#30363D] px-3 py-2 flex items-center gap-3" data-testid="lot-coverage-panel">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad"><LandPlot className="w-3 h-3" /> Lot</div>
            <div className="flex items-center gap-1">
              <Input type="number" value={Math.round(effLot.w)} onChange={(e) => setLot({ ...effLot, w: Math.max(10, Number(e.target.value) || 10) })} className="h-7 w-16 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs px-2" data-testid="lot-width-field" />
              <span className="text-[#6E7681] text-xs">×</span>
              <Input type="number" value={Math.round(effLot.h)} onChange={(e) => setLot({ ...effLot, h: Math.max(10, Number(e.target.value) || 10) })} className="h-7 w-16 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs px-2" data-testid="lot-depth-field" />
              <span className="text-[#6E7681] text-xs">ft</span>
            </div>
            <button onClick={() => setLotDialogOpen(true)} data-testid="lot-address-btn" className="flex items-center gap-1 text-xs text-[#38BDF8] hover:text-[#7DD3FC]" title="Estimate lot from an address (AI)">
              <Wand2 className="w-3.5 h-3.5" /> {lotAddress ? "Edit address" : "From address"}
            </button>
            <div className="h-5 w-px bg-[#30363D]" />
            {coverage && (
              <div className="flex items-center gap-1.5" data-testid="coverage-meter" title={`${coverage.footprint} ft² footprint / ${coverage.lotArea} ft² lot · limit ${coverageLimit}%`}>
                <span className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">Coverage</span>
                <span className={`text-sm font-mono-cad font-bold ${coverage.pct > coverageLimit ? "text-[#EF4444]" : coverage.pct > coverageLimit * 0.9 ? "text-[#F59E0B]" : "text-[#34D399]"}`} data-testid="coverage-value">
                  {coverage.pct.toFixed(1)}%
                </span>
                <span className="text-[10px] text-[#6E7681] font-mono-cad">/ {coverageLimit}%</span>
                {coverage.pct > coverageLimit && <AlertTriangle className="w-3.5 h-3.5 text-[#EF4444]" data-testid="coverage-over-warning" />}
              </div>
            )}
          </div>

          {/* Layer toggles overlay (roll-out) */}
          <div className="absolute top-4 left-4 z-10" data-testid="layer-panel">
            <button onClick={() => setLayersOpen((v) => !v)} data-testid="layers-toggle-btn"
              className="glass rounded-xl border border-[#30363D] px-3 py-2 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#8B949E] font-mono-cad hover:text-[#F0F6FC] transition-colors">
              <Layers className="w-3 h-3" /> Layers
              <ChevronDown className={`w-3 h-3 transition-transform ${layersOpen ? "rotate-180" : ""}`} />
            </button>
            {layersOpen && (
              <div className="mt-1.5 glass rounded-xl border border-[#30363D] p-2 space-y-1 animate-fade-up" data-testid="layer-list">
                {Object.entries(LAYERS).map(([l, cfg]) => (
                  <button key={l} onClick={() => toggleLayer(l)} data-testid={`cad-layer-${l}`}
                    className="flex items-center gap-2 w-full px-2 py-1 rounded-md hover:bg-[#21262D] transition-colors text-sm">
                    {activeLayers[l] ? <Eye className="w-3.5 h-3.5" style={{ color: cfg.accent }} /> : <EyeOff className="w-3.5 h-3.5 text-[#6E7681]" />}
                    <span style={{ color: activeLayers[l] ? cfg.accent : "#6E7681" }}>{cfg.label}</span>
                  </button>
                ))}
                {site?.image_path && (
                  <button onClick={() => setShowSiteMap((v) => !v)} data-testid="cad-layer-sitemap"
                    className="flex items-center gap-2 w-full px-2 py-1 rounded-md hover:bg-[#21262D] transition-colors text-sm border-t border-[#30363D] mt-1 pt-1.5">
                    {showSiteMap ? <Eye className="w-3.5 h-3.5 text-[#F43F5E]" /> : <EyeOff className="w-3.5 h-3.5 text-[#6E7681]" />}
                    <span style={{ color: showSiteMap ? "#F43F5E" : "#6E7681" }}>Site Map</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sun-path season selector */}
          {sunPath && (
            <div className="absolute top-4 right-4 glass rounded-xl border border-[#F59E0B]/40 p-2" data-testid="season-selector">
              <div className="flex items-center gap-1.5 px-1 pb-1.5 text-[10px] uppercase tracking-widest text-[#F59E0B] font-mono-cad"><Sunrise className="w-3 h-3" /> Season</div>
              <div className="flex gap-1">
                {["Summer", "Equinox", "Winter"].map((s) => (
                  <button key={s} onClick={() => setSeason(s)} data-testid={`season-${s}`}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${season === s ? "bg-[#F59E0B] text-[#0D1117]" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Clearance warning badge */}
          {clearance.conflicts.length > 0 && (
            <div className="absolute top-4 right-4 glass rounded-lg border border-[#F43F5E]/50 px-3 py-2 flex flex-col gap-2 text-xs text-[#F43F5E] max-w-[240px]" style={{ top: sunPath ? "88px" : "16px" }} data-testid="clearance-warning">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="font-medium">{clearance.conflicts.length} piece(s) block a doorway/window</span>
              </div>
              <Button size="sm" onClick={autoFixClearance} data-testid="autofix-clearance-btn" className="h-7 bg-[#F43F5E] hover:bg-[#E11D48] text-white font-semibold w-full">
                <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Auto-Fix Clearance
              </Button>
            </div>
          )}

          {/* Zoom */}
          <div className="absolute bottom-4 left-4 glass rounded-lg border border-[#30363D] flex items-center">
            <button onClick={() => setPxPerFt((z) => Math.max(6, z - 2))} className="p-2 text-[#8B949E] hover:text-[#F0F6FC]" data-testid="zoom-out-btn"><ZoomOut className="w-4 h-4" /></button>
            <span className="text-xs font-mono-cad text-[#8B949E] w-12 text-center">{Math.round(pxPerFt / 13 * 100)}%</span>
            <button onClick={() => setPxPerFt((z) => Math.min(30, z + 2))} className="p-2 text-[#8B949E] hover:text-[#F0F6FC]" data-testid="zoom-in-btn"><ZoomIn className="w-4 h-4" /></button>
          </div>

          {/* Stats */}
          <div className="absolute bottom-4 right-4 glass rounded-lg border border-[#30363D] px-4 py-2 font-mono-cad text-xs text-[#8B949E]" data-testid="area-stats">
            <span className="text-[#38BDF8]">{totalArea(blueprint.rooms)}</span> ft² · {blueprint.rooms.length} rooms · {blueprint.items.length} items
          </div>

          {/* Sun & shadow time slider */}
          {showShadows && (
            <div className="absolute bottom-4 left-4 glass rounded-xl border border-[#FBBF24]/40 px-4 py-2.5 w-64" data-testid="shadow-slider-panel">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-[#FBBF24] flex items-center gap-1.5"><Sun className="w-3.5 h-3.5" /> Sun & Shadow</span>
                <span className="text-[11px] font-mono-cad text-[#8B949E]" data-testid="shadow-time-label">{shadow.hour < 12 ? `${shadow.hour}am` : shadow.hour === 12 ? "12pm" : `${shadow.hour - 12}pm`} · {shadow.altitude}° alt</span>
              </div>
              <input type="range" min="0" max="1" step="0.01" value={sunTime} onChange={(e) => setSunTime(Number(e.target.value))} className="w-full accent-[#FBBF24]" data-testid="shadow-time-slider" />
              <div className="flex justify-between text-[10px] text-[#6E7681] font-mono-cad mt-0.5"><span>6am</span><span>noon</span><span>6pm</span></div>
            </div>
          )}

          {/* Live setback violation warning */}
          {setbackViol && setbackViol.count > 0 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 rounded-lg border border-[#F43F5E] bg-[#F43F5E]/15 px-3 py-1.5 flex items-center gap-2 animate-fade-up" data-testid="setback-warning-chip">
              <AlertTriangle className="w-4 h-4 text-[#F43F5E]" />
              <span className="text-xs font-semibold text-[#F43F5E]">{setbackViol.count} room{setbackViol.count > 1 ? "s" : ""} cross the {effSetbacks?.front || 0}′ front / {effSetbacks?.side || 0}′ side setback</span>
            </div>
          )}

          {/* Dimension-drift warning (Dim Guard) */}
          {dimGuard && driftCount > 0 && (
            <div className="absolute top-14 left-1/2 -translate-x-1/2 rounded-lg border border-[#F59E0B] bg-[#F59E0B]/15 px-3 py-1.5 flex items-center gap-2 animate-fade-up" data-testid="dim-drift-chip">
              <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-xs font-semibold text-[#F59E0B]">{driftCount} room{driftCount > 1 ? "s" : ""} drifted from the locked plan size</span>
            </div>
          )}

          {/* Trace-my-house CTA when a survey background is present */}
          {site?.image_path && showSiteMap && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 glass rounded-xl border border-[#F43F5E]/40 px-3 py-2 flex items-center gap-2" data-testid="trace-cta">
              {blueprint.rooms.length === 0 ? (
                <>
                  <span className="text-xs text-[#F0F6FC] flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5 text-[#F43F5E]" /> Trace your house on the survey</span>
                  <Button size="sm" onClick={autoFitHouse} data-testid="autofit-house-btn" className="h-7 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Auto-Fit House</Button>
                </>
              ) : (
                <>
                  <span className="text-xs text-[#8B949E]">Footprint traced?</span>
                  <Button size="sm" onClick={suggestFromGrade} data-testid="trace-foundation-btn" className="h-7 bg-[#F43F5E] hover:bg-[#e11d48] text-white font-semibold">
                    <Mountain className="w-3.5 h-3.5 mr-1.5" /> Trace → Foundation
                  </Button>
                </>
              )}
            </div>
          )}
          </div>
          {splitView && (
            <LivePreview blueprint={blueprint} name={name} roofStyle={roofStyle} floors={floors} lot={effLot} sourcePhoto={sourcePhotoPath} terrain={siteTerrainDesc()} onClose={() => setSplitView(false)} />
          )}
        </main>

        {/* Right inspector */}
        <aside className="w-64 shrink-0 border-l border-[#30363D] bg-[#161B22] p-4 overflow-y-auto hidden lg:block" data-testid="inspector-panel">
          <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-[#8B949E] mb-3">Inspector</h3>
          {renderPath && (
            <img src={`${api.defaults.baseURL}/files/${renderPath}`} alt="render" className="w-full h-32 object-cover rounded-lg border border-[#30363D] mb-4" />
          )}
          {videoPath && (
            <div className="mb-4" data-testid="inspector-video">
              <div className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad mb-1 flex items-center gap-1"><Film className="w-3 h-3" /> Walkabout</div>
              <video src={`${api.defaults.baseURL}/files/${videoPath}`} controls loop muted playsInline className="w-full rounded-lg border border-[#30363D] bg-black" />
            </div>
          )}
          {!selectedId && (
            <div className="text-sm text-[#6E7681] leading-relaxed">
              <Home className="w-8 h-8 mb-2 text-[#484F58]" />
              Select an element to edit it, or pick a tool to draw. Use <span className="text-[#38BDF8]">Draw Room</span> to click-drag a new room.
            </div>
          )}
          {selRoom && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#8B949E]">Room Name</label>
                <Input value={selRoom.name} onChange={(e) => updRoom({ name: e.target.value })} className="bg-[#0D1117] border-[#30363D] h-9" data-testid="room-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[["x", "X (ft)"], ["y", "Y (ft)"], ["w", "Width (ft)"], ["h", "Height (ft)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-xs text-[#8B949E]">{l}</label>
                    <Input type="number" step="0.5" value={selRoom[k]} onChange={(e) => updRoom({ [k]: Math.max(0, Number(e.target.value)) })} className="bg-[#0D1117] border-[#30363D] h-9 font-mono-cad" data-testid={`room-${k}-input`} />
                  </div>
                ))}
              </div>
              <div className="text-xs font-mono-cad text-[#8B949E] bg-[#0D1117] rounded-md p-2 border border-[#30363D]">Area: <span className="text-[#38BDF8]">{Math.round(selRoom.w * selRoom.h)} ft²</span></div>
              <Button variant="outline" onClick={deleteSelected} className="w-full border-[#30363D] text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]" data-testid="delete-element-btn"><Trash2 className="w-4 h-4 mr-1.5" /> Delete Room</Button>
            </div>
          )}
          {selItem && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full flex items-center justify-center font-mono-cad font-bold text-xs" style={{ color: LAYERS[selItem.layer].accent, border: `2px solid ${LAYERS[selItem.layer].accent}` }}>{ITEM_KINDS[selItem.kind].label}</span>
                <div>
                  <div className="font-heading font-semibold text-sm">{ITEM_KINDS[selItem.kind].title}</div>
                  <div className="text-xs" style={{ color: LAYERS[selItem.layer].accent }}>{LAYERS[selItem.layer].label}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[["x", "X (ft)"], ["y", "Y (ft)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-xs text-[#8B949E]">{l}</label>
                    <Input type="number" step="0.5" value={selItem[k]} onChange={(e) => updItem({ [k]: Math.max(0, Number(e.target.value)) })} className="bg-[#0D1117] border-[#30363D] h-9 font-mono-cad" />
                  </div>
                ))}
                {ITEM_KINDS[selItem.kind].furniture && [["w", "Width (ft)"], ["h", "Depth (ft)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-xs text-[#8B949E]">{l}</label>
                    <Input type="number" step="0.5" value={selItem[k] ?? ITEM_KINDS[selItem.kind][k]} onChange={(e) => updItem({ [k]: Math.max(1, Number(e.target.value)) })} className="bg-[#0D1117] border-[#30363D] h-9 font-mono-cad" />
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={deleteSelected} className="w-full border-[#30363D] text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]" data-testid="delete-element-btn"><Trash2 className="w-4 h-4 mr-1.5" /> Delete Item</Button>
            </div>
          )}
          {selWall && (
            <div className="space-y-3" data-testid="wall-inspector">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ color: "#C9D1D9", border: "2px solid #C9D1D9" }}><PenLine className="w-4 h-4" /></span>
                <div>
                  <div className="font-heading font-semibold text-sm">Wall Segment</div>
                  <div className="text-xs text-[#8B949E] font-mono-cad">{Math.round(Math.hypot(selWall.x2 - selWall.x1, selWall.y2 - selWall.y1) * 10) / 10}′ long</div>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#8B949E]">Thickness (ft)</label>
                <Input type="number" step="0.1" min="0.2" max="1.5" value={selWall.thickness} onChange={(e) => updWall({ thickness: Math.max(0.2, Math.min(1.5, Number(e.target.value) || 0.4)) })} className="bg-[#0D1117] border-[#30363D] h-9 font-mono-cad" data-testid="wall-thickness-input" />
              </div>
              <div className="flex gap-1.5">
                {[["Interior", 0.4], ["Exterior", 0.6], ["Thick", 0.9]].map(([lbl, t]) => (
                  <button key={lbl} onClick={() => updWall({ thickness: t })} data-testid={`wall-preset-${t}`}
                    className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${Math.abs(selWall.thickness - t) < 0.01 ? "bg-[#38BDF8] text-[#0D1117]" : "bg-[#0D1117] border border-[#30363D] text-[#8B949E] hover:text-[#F0F6FC]"}`}>{lbl}</button>
                ))}
              </div>
              <Button variant="outline" onClick={splitWall} className="w-full border-[#30363D] hover:border-[#38BDF8] hover:text-[#38BDF8]" data-testid="wall-split-btn"><Scissors className="w-4 h-4 mr-1.5" /> Split Wall</Button>
              <Button variant="outline" onClick={deleteSelected} className="w-full border-[#30363D] text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]" data-testid="delete-element-btn"><Trash2 className="w-4 h-4 mr-1.5" /> Delete Wall</Button>
            </div>
          )}
          {selRun && RUN_KINDS[selRun.tool] && (
            <div className="space-y-3" data-testid="run-inspector">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ color: RUN_KINDS[selRun.tool].color, border: `2px solid ${RUN_KINDS[selRun.tool].color}` }}>
                  {RUN_KINDS[selRun.tool].layer === "hvac" ? <Wind className="w-4 h-4" /> : <Droplets className="w-4 h-4" />}
                </span>
                <div>
                  <div className="font-heading font-semibold text-sm">{RUN_KINDS[selRun.tool].label}</div>
                  <div className="text-xs font-mono-cad" style={{ color: RUN_KINDS[selRun.tool].color }}>
                    {LAYERS[RUN_KINDS[selRun.tool].layer].label} · {Math.round(Math.hypot(selRun.x2 - selRun.x1, selRun.y2 - selRun.y1) * 10) / 10}′ run
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(RUN_KINDS).filter(([, c]) => c.layer === RUN_KINDS[selRun.tool].layer).map(([k, c]) => (
                  <button key={k} onClick={() => setBlueprint((b) => ({ ...b, runs: (b.runs || []).map((r) => r.id === selectedId ? { ...r, tool: k } : r) }))} data-testid={`run-type-${k}`}
                    className={`px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors ${selRun.tool === k ? "text-[#0D1117]" : "bg-[#0D1117] border border-[#30363D] text-[#8B949E] hover:text-[#F0F6FC]"}`}
                    style={{ background: selRun.tool === k ? c.color : undefined }}>{c.label}</button>
                ))}
              </div>
              <Button variant="outline" onClick={deleteSelected} className="w-full border-[#30363D] text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]" data-testid="delete-element-btn"><Trash2 className="w-4 h-4 mr-1.5" /> Delete Run</Button>
            </div>
          )}
        </aside>
        <AIAssistant open={assistantOpen} onClose={() => setAssistantOpen(false)}
          context={{ city: city === "Custom" ? "" : city, state: usState, lot: effLot, floors_count: floors.length, rooms_count: blueprint.rooms.length, code_summary: codeResearch?.summary || "",
            current_floors: floors.map((f) => ({ name: f.name, level: f.level, rooms: (f.rooms || []).map((r) => ({ name: r.name, x: r.x, y: r.y, w: r.w, h: r.h })) })) }}
          onApplyFloors={applyFloors} onRunTool={runAssistantTool} />
        </>
        )}
      </div>

      <AIGeneratorModal open={aiOpen} onOpenChange={setAiOpen} onApply={applyAI} city={[city === "Custom" ? "" : city, usState].filter(Boolean).join(", ") || "Custom"} />
      <PhotoReconstructor open={photoOpen} onOpenChange={setPhotoOpen} onApply={applyAI} onApplyFloors={applyFloors} city={[city === "Custom" ? "" : city, usState].filter(Boolean).join(", ") || "Custom"} />
      <OrdinanceChecker open={ordOpen} onOpenChange={setOrdOpen} blueprint={blueprint} city={city} setCity={setCity} lot={effLot} stories={floors.length} floorHeight={floorHeight} />
      <CodeResearch open={codeOpen} onOpenChange={setCodeOpen} state={usState} setState={setUsState} city={city} setCity={setCity} research={codeResearch} setResearch={setCodeResearch} />
      <ExportModal open={exportOpen} onOpenChange={setExportOpen} project={{ name, city, blueprint, site, floors, lot: effLot }} />
      <Walkthrough3D open={waOpen} onOpenChange={setWaOpen} blueprint={blueprint} floors={floors} roofStyle={roofStyle} onRoofStyleChange={setRoofStyle} site={site} />
      <RenderModal open={renderOpen} onOpenChange={(v) => { setRenderOpen(v); if (!v) setRenderAuto(false); }} project={{ name, city, blueprint, roofStyle, floors, lot: effLot, sourcePhoto: sourcePhotoPath, terrain: siteTerrainDesc(), gallery: site?.gallery || [] }}
        autoGenerate={renderAuto} onVideoReady={setVideoPath} tourClips={tourClips} setTourClips={setTourClips}
        shots={videoShots} setShots={setVideoShots} />
      <CostEstimator open={costOpen} onOpenChange={setCostOpen} blueprint={blueprint} stories={floors.length} site={site} floors={floors} lot={effLot} defaultTier={site?.cost_tier || "Standard"} />
      <SiteDesigner open={siteOpen} onOpenChange={setSiteOpen} onApply={applyFloors} city={city} />
      <CrossSection open={sectionOpen} onOpenChange={setSectionOpen} blueprint={blueprint} floors={floors} site={site} />
      <SolarScore open={solarOpen} onOpenChange={setSolarOpen} blueprint={blueprint} season={season} setSeason={setSeason} />

      <Dialog open={lotDialogOpen} onOpenChange={setLotDialogOpen}>
        <DialogContent className="max-w-md bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-xl"><LandPlot className="w-5 h-5 text-[#38BDF8]" /> Lot From Address</DialogTitle>
            <DialogDescription className="text-[#8B949E]">Type a property address and AI estimates the parcel size. You can fine-tune the width/depth or drag the property line afterwards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs text-[#8B949E]">Property Address</Label>
              <Input value={lotAddress} onChange={(e) => setLotAddress(e.target.value)} placeholder="e.g. 1600 Amphitheatre Pkwy, Mountain View, CA" className="bg-[#0D1117] border-[#30363D] mt-1" data-testid="lot-address-input" />
            </div>
            <div>
              <Label className="text-xs text-[#8B949E]">Floor-to-floor height (ft)</Label>
              <Input type="number" step="0.5" value={floorHeight} onChange={(e) => setFloorHeight(Math.max(6, Number(e.target.value) || 10))} className="bg-[#0D1117] border-[#30363D] mt-1 font-mono-cad" data-testid="floor-height-input" />
              <p className="text-[11px] text-[#6E7681] mt-1">Used for the Building Height compliance check ({floors.length} floor(s) x {floorHeight}ft = {Math.round(floors.length * floorHeight)}ft tall).</p>
            </div>
            <p className="text-[11px] text-[#6E7681] leading-relaxed">Pulls the recorded parcel dimensions for the address (via Regrid); if none is found it falls back to an AI estimate. Frontage/depth are approximate — confirm with a survey.</p>
          </div>
          <DialogFooter>
            <Button onClick={estimateLot} disabled={lotEstimating} className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="lot-estimate-btn">
              {lotEstimating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching…</> : <><Wand2 className="w-4 h-4 mr-2" /> Fetch Lot</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <DialogContent className="max-w-sm bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-lg">
              {confirmAction === "all" ? <><FilePlus2 className="w-5 h-5 text-[#EF4444]" /> Start a new design?</> : <><Eraser className="w-5 h-5 text-[#F59E0B]" /> Clear this floor?</>}
            </DialogTitle>
            <DialogDescription className="text-[#8B949E]">
              {confirmAction === "all"
                ? "This clears the current design from your screen (all floors, lot, codes and settings) so you can start fresh. Anything you've already Saved stays safe in your Dashboard."
                : `This removes every room, wall and item on "${_activeFloor.name || "this floor"}". Other floors are not affected.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmAction(null)} data-testid="confirm-cancel-btn" className="border-[#30363D] bg-transparent">Cancel</Button>
            {confirmAction === "all"
              ? <Button onClick={startOver} data-testid="confirm-startover-btn" className="bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold">Start Over</Button>
              : <Button onClick={clearFloor} data-testid="confirm-clearfloor-btn" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0D1117] font-semibold">Clear Floor</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="max-w-sm bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Pencil className="w-4 h-4 text-[#38BDF8]" /> Rename project</DialogTitle>
          </DialogHeader>
          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") confirmRename(); }} autoFocus className="bg-[#0D1117] border-[#30363D] font-heading" data-testid="rename-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRenameTarget(null)} className="border-[#30363D] bg-transparent">Cancel</Button>
            <Button onClick={confirmRename} data-testid="rename-confirm-btn" className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-[#EF4444]"><Trash2 className="w-4 h-4" /> Delete project</DialogTitle>
            <DialogDescription className="text-[#8B949E]">Delete “{deleteTarget?.name}”? This can't be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-[#30363D] bg-transparent">Cancel</Button>
            <Button onClick={confirmDelete} data-testid="delete-confirm-btn" className="bg-[#EF4444] hover:bg-[#dc2626] text-white font-semibold">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!gradeSuggestion} onOpenChange={(o) => !o && setGradeSuggestion(null)}>
        <DialogContent className="max-w-md bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-lg"><Mountain className="w-5 h-5 text-[#F43F5E]" /> Grade → Foundation</DialogTitle>
            <DialogDescription className="text-[#8B949E]">A foundation suggestion engineered from your site's slope and elevations.</DialogDescription>
          </DialogHeader>
          {gradeSuggestion && (
            <div className="space-y-3 py-1" data-testid="grade-suggestion">
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Slope", gradeSuggestion.slope], ["Grade fall", `${gradeSuggestion.drop} ft`], ["Steps", gradeSuggestion.steps]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-[#30363D] bg-[#0D1117] p-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">{l}</div>
                    <div className="text-sm font-heading font-semibold text-[#F0F6FC]">{v}</div>
                  </div>
                ))}
              </div>
              <ul className="text-xs text-[#8B949E] space-y-1.5 leading-relaxed list-disc pl-4">
                {gradeSuggestion.hasFoundation ? (
                  <li className="text-[#6E7681]">A foundation level already exists — it won't be duplicated.</li>
                ) : (
                  <li>Adds a <span className="text-[#F0F6FC]">Stepped Foundation</span> level with {gradeSuggestion.steps + 1} rows of footings stepping down the slope, a high-side retaining wall and stem walls.</li>
                )}
                {gradeSuggestion.walkout ? (
                  gradeSuggestion.hasBasement
                    ? <li className="text-[#6E7681]">A basement already exists — the walk-out won't be duplicated.</li>
                    : <li>The ~{gradeSuggestion.drop} ft of fall supports a <span className="text-[#F0F6FC]">Walk-Out Basement</span> — adds a lower level with a downhill door + daylight windows.</li>
                ) : (
                  <li className="text-[#6E7681]">Not enough fall for a walk-out basement (needs ≥ 6 ft on a sloped lot).</li>
                )}
              </ul>
              <div className="rounded-lg border border-[#F43F5E]/40 bg-[#F43F5E]/5 p-3" data-testid="grade-cutfill">
                <div className="text-[10px] uppercase tracking-widest text-[#F43F5E] font-mono-cad mb-1.5 flex items-center gap-1.5"><Mountain className="w-3 h-3" /> Earthwork · Cut &amp; Fill</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[["Cut", `${gradeSuggestion.cutCy} cy`], ["Fill", `${gradeSuggestion.fillCy} cy`], ["Est. cost", `$${(gradeSuggestion.ewCost || 0).toLocaleString()}`]].map(([l, v]) => (
                    <div key={l}>
                      <div className="text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">{l}</div>
                      <div className="text-sm font-heading font-semibold text-[#F0F6FC]">{v}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[#6E7681] mt-1.5">Over a {gradeSuggestion.area.toLocaleString()} ft² pad — balanced cut/fill, excavation ${45}/cy + compacted fill ${25}/cy. Full takeoff lives in the Cost tool.</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGradeSuggestion(null)} data-testid="grade-cancel-btn" className="border-[#30363D] bg-transparent">Cancel</Button>
            <Button onClick={applyGradeSuggestion} data-testid="grade-apply-btn" className="bg-[#F43F5E] hover:bg-[#e11d48] text-white font-semibold">Add Foundation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={saveDialogOpen} onOpenChange={(o) => { setSaveDialogOpen(o); if (!o) setSaveOk(false); }}>        <DialogContent className="max-w-sm bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-lg"><Save className="w-5 h-5 text-[#38BDF8]" /> Save Project</DialogTitle>
            <DialogDescription className="text-[#8B949E]">Name your project and save it to your account. It'll appear on your Dashboard.</DialogDescription>
          </DialogHeader>
          {saveOk ? (
            <div className="space-y-4 py-2" data-testid="save-success">
              <div className="flex items-center gap-2 text-[#34D399]"><CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Saved "{name}" to your account</span></div>
              <p className="text-xs text-[#8B949E]">Your work is safely stored. You can find it any time on your Dashboard.</p>
              <div className="flex gap-2">
                <Link to="/dashboard" className="flex-1"><Button className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="save-open-dashboard-btn"><LayoutGrid className="w-4 h-4 mr-1.5" /> Open Dashboard</Button></Link>
                <Button variant="outline" onClick={() => setSaveDialogOpen(false)} data-testid="save-keep-editing-btn" className="border-[#30363D] bg-transparent">Keep editing</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-1">
              <div>
                <Label className="text-xs text-[#8B949E]">Project name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") doSaveFromDialog(); }} placeholder="e.g. Maple Street Residence" autoFocus className="bg-[#0D1117] border-[#30363D] mt-1 font-heading font-semibold" data-testid="save-name-input" />
              </div>
              <Button onClick={doSaveFromDialog} disabled={saving} className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="save-confirm-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> {projectId ? "Update saved project" : "Save Project"}</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
